import { PencilIcon } from 'morpheus-ui';
import { useEffect, useState } from 'react';
import { VariantProps } from 'tailwind-variants';
import { Tooltip } from 'react-tooltip';
import { getFirstViewedLabel } from '../../../../utils/dataTools';
import { Button } from '../../../atoms/Button';
import { Icon } from '../../../atoms/Icon';
import { Tag, TagVariant } from '../../../atoms/Tag';
import { Typography } from '../../../molecules/Typography';
import { useNewContentCardContext } from '../useNewContentCardContext';
import { IconDone, SubjectTag } from '../variants';

interface INewContentCardHeaderProps {
  done: boolean;
  firstViewedAt?: string | null;
  canClickDone?: boolean;
  onClickDone: (contentId: string) => void | Promise<void>;
  showSubject?: boolean;
  showAnswer?: boolean;
}

export function NewContentCardHeader({
  done,
  firstViewedAt,
  canClickDone = true,
  onClickDone,
  showSubject = false,
  // showAnswer = false,
}: INewContentCardHeaderProps) {
  const [isDone, setIsDone] = useState(done);
  const [isLoadingDone, setIsLoadingDone] = useState(false);
  const { contentType, contentId, subject, discipline } =
    useNewContentCardContext();

  // Tooltip com a data em que o conteúdo foi visto pela primeira vez.
  const firstViewedLabel = getFirstViewedLabel(firstViewedAt);

  const materialTypesInfos = {
    exercise: { label: 'Exercício', icon: 'mode' },
    essay: { label: 'Enviar Redação', icon: PencilIcon },
    material: { label: 'Material', icon: 'library_books' },
    lesson: { label: 'Videoaula', icon: 'play_circle' },
  };

  const materialInfo =
    materialTypesInfos[contentType as keyof typeof materialTypesInfos];

  useEffect(() => {
    setIsDone(done);
    setIsLoadingDone(false);
  }, [done]);

  return (
    <header className='flex items-center justify-between gap-lg'>
      <div className='flex items-center gap-sm'>
        <Icon
          Icon={materialInfo.icon}
          type={typeof materialInfo.icon === 'string' ? 'material' : 'svg'}
          className='w-5 text-lg text-brand-color-secondary-pure transition delay-100 ease-in-out group-hover:text-card-color-default'
        />
        <Typography
          type='titleSection'
          size='h6'
          className={`w-full transition delay-100 ease-in-out group-hover:text-card-color-default`}
        >
          {materialInfo.label}
        </Typography>
      </div>
      <div className='flex max-w-[60%] items-center gap-lg'>
        {showSubject && subject && (
          <Tag
            variant={(discipline as TagVariant) || 'neutral'}
            className={SubjectTag({
              color: discipline as VariantProps<typeof SubjectTag>['color'],
            })}
            style={{
              overflowWrap: 'anywhere',
            }}
          >
            <Typography
              size='small'
              className='line-clamp-1 min-w-fit text-mono-color-white transition delay-100 ease-in-out group-hover:text-card-color-negative'
            >
              {subject}
            </Typography>
          </Tag>
        )}
        <Button
          variant='text'
          onlyIcon
          LeftIcon={'check_circle'}
          loading={isLoadingDone}
          data-tooltip-id={
            firstViewedLabel ? `first-viewed-${contentId}` : undefined
          }
          data-tooltip-content={firstViewedLabel ?? undefined}
          classNameIcon={IconDone({
            type: isDone ? 'seen' : 'unseen',
          })}
          className='p-0'
          onClick={async (e) => {
            if (!canClickDone || isLoadingDone) return;
            e.stopPropagation();
            setIsLoadingDone(true);
            try {
              await onClickDone(contentId);
            } catch (err) {
              console.error('Erro ao marcar conteúdo como concluído', err);
            } finally {
              // Garante que o loading sempre é encerrado, mesmo se o `done` não
              // mudar (item já concluído) ou o request falhar.
              setIsLoadingDone(false);
            }
          }}
        />
        {firstViewedLabel && (
          <Tooltip
            id={`first-viewed-${contentId}`}
            place='top'
            opacity={1}
            style={{
              background: '#2E3847',
              color: '#fff',
              borderRadius: '8px',
              padding: '6px 10px',
              fontSize: '12px',
              zIndex: 20,
            }}
          />
        )}
      </div>
    </header>
  );
}
